import { StatStrip } from "@nimit9/signet-ui/components/stat-strip"
import { useMediaQuery } from "@nimit9/signet-ui/hooks/use-media-query"
import { createFileRoute } from "@tanstack/react-router"
import { useEffect, useMemo, useRef, useState } from "react"
import { Bar, BarChart, CartesianGrid, XAxis, YAxis } from "recharts"
import {
  AXIS_PROPS,
  BAR_CURSOR,
  BAR_RADIUS,
  CategoryLegend,
  CHART_MARGIN,
  ChartCard,
  type ChartConfig,
  ChartFrame,
  ChartTooltip,
  GRID_PROPS,
  legendItemsFromConfig,
  MoneyTooltip,
  Y_AXIS_WIDTH,
} from "@/components/charts"
import { Amount } from "@/components/common/amount"
import { useCompactCurrencyFormat } from "@/hooks/use-compact-currency-format"
import { useInvestments } from "@/hooks/use-investments"
import { computeSipSchedule } from "./-compute"
import { buildSipHeadline } from "./-headline"
import { CalculatorLayout, NumberField, SliderField } from "./-layout"

export const Route = createFileRoute("/calculators/sip")({
  component: SIPCalculator,
})

const CHART_CONFIG = {
  Invested: { label: "Invested", color: "var(--chart-2)" },
  Value: { label: "Portfolio value", color: "var(--money-credit)" },
} satisfies ChartConfig

function SIPCalculator() {
  const isDesktop = useMediaQuery("(min-width: 768px)")
  const fmtCompact = useCompactCurrencyFormat(2)
  const { data: investData } = useInvestments()
  const [prefilled, setPrefilled] = useState(false)
  const [monthly, setMonthly] = useState(10000)
  const [stepUp, setStepUp] = useState(10)
  const [returns, setReturns] = useState(12)
  const [years, setYears] = useState(20)

  const investApplied = useRef(false)
  useEffect(() => {
    if (investApplied.current || !investData?.sips?.all?.length) return
    let applied = false
    // Use all SIPs (active + paused) — paused SIPs still represent the user's investment habit
    const allSipsTotal = investData.sips.all.reduce((s, sip) => s + parseFloat(sip.amount), 0)
    if (allSipsTotal > 0) {
      setMonthly(Math.round(allSipsTotal / 500) * 500 || 10000)
      applied = true
    }
    // Use average step-up from SIPs that have one
    const sipsWithStepUp = investData.sips.all.filter(
      (s) => s.stepUpPercent && parseFloat(s.stepUpPercent) > 0,
    )
    if (sipsWithStepUp.length > 0) {
      const avgStepUp =
        sipsWithStepUp.reduce((s, sip) => s + parseFloat(sip.stepUpPercent ?? "0"), 0) /
        sipsWithStepUp.length
      setStepUp(Math.round(avgStepUp))
      applied = true
    }
    if (applied) {
      investApplied.current = true
      setPrefilled(true)
    }
  }, [investData])

  const result = useMemo(
    () => computeSipSchedule(monthly, stepUp, returns, years),
    [monthly, stepUp, returns, years],
  )
  const { totalInvested, finalValue, yearlyData } = result
  const gains = finalValue - totalInvested
  const multiple = totalInvested > 0 ? (finalValue / totalInvested).toFixed(1) : "0.0"
  const gainsPct = totalInvested > 0 ? ((gains / totalInvested) * 100).toFixed(0) : "0"
  const headline = buildSipHeadline(result, years)

  return (
    <CalculatorLayout
      title="SIP Calculator"
      headline={headline}
      isDesktop={isDesktop}
      prefilledNote={prefilled ? "Pre-filled from your SIPs" : null}
      inputs={
        <>
          <NumberField
            id="sip-monthly"
            label="Monthly SIP (₹)"
            value={monthly}
            onChange={setMonthly}
            clamp={(n) => Math.max(500, n)}
          />
          <SliderField
            id="sip-stepup"
            label="Annual step-up"
            value={stepUp}
            onChange={setStepUp}
            min={0}
            max={30}
            format={(n) => `${n}%`}
            minLabel="0%"
            maxLabel="30%"
          />
          <SliderField
            id="sip-returns"
            label="Expected return"
            value={returns}
            onChange={setReturns}
            min={4}
            max={24}
            format={(n) => `${n}% p.a.`}
            minLabel="4%"
            maxLabel="24%"
          />
          <SliderField
            id="sip-years"
            label="Duration"
            value={years}
            onChange={setYears}
            min={1}
            max={40}
            format={(n) => `${n} years`}
            minLabel="1Y"
            maxLabel="40Y"
          />
        </>
      }
    >
      <StatStrip
        isDesktop={isDesktop}
        items={[
          { label: "Total invested", value: <Amount value={Math.round(totalInvested)} /> },
          {
            label: "Final value",
            value: <Amount value={Math.round(finalValue)} />,
            meta: `${multiple}× growth`,
            tone: "good",
          },
          {
            label: "Wealth gained",
            value: <Amount value={Math.round(gains)} />,
            meta: `${gainsPct}% gains`,
            tone: gains >= 0 ? "good" : "warn",
          },
        ]}
      />
      <ChartCard
        title="Invested vs portfolio value"
        legend={<CategoryLegend items={legendItemsFromConfig(CHART_CONFIG)} />}
        isEmpty={yearlyData.length === 0}
      >
        <ChartFrame config={CHART_CONFIG}>
          <BarChart data={yearlyData} margin={CHART_MARGIN} barCategoryGap="20%">
            <CartesianGrid {...GRID_PROPS} />
            <XAxis
              dataKey="year"
              {...AXIS_PROPS}
              interval={isDesktop ? 0 : Math.max(0, Math.ceil(yearlyData.length / 5) - 1)}
            />
            <YAxis {...AXIS_PROPS} width={Y_AXIS_WIDTH + 8} tickFormatter={fmtCompact} />
            <ChartTooltip content={<MoneyTooltip showTotal={false} />} cursor={BAR_CURSOR} />
            <Bar
              dataKey="Invested"
              name="Invested"
              fill="var(--color-Invested)"
              radius={BAR_RADIUS}
              isAnimationActive={false}
            />
            <Bar
              dataKey="Value"
              name="Portfolio value"
              fill="var(--color-Value)"
              radius={BAR_RADIUS}
              isAnimationActive={false}
            />
          </BarChart>
        </ChartFrame>
      </ChartCard>
    </CalculatorLayout>
  )
}
